import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { 
  ResponsiveTable, 
  ResponsiveTableHeader, 
  ResponsiveTableBody, 
  ResponsiveTableRow, 
  ResponsiveTableCell, 
  ResponsiveTableHeaderCell 
} from '@/components/ui/responsive-table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Phone,
  Mail,
  MessageCircle,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  MapPin
} from 'lucide-react'
import { supabase } from '@/lib/supabase'

// Database types based on existing schema
interface Lead {
  id: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  service_street?: string;
  message?: string;
  service_requested?: string;
  service_slug?: string;
  location?: string;
  submission_url?: string;
  form_id?: string;
  form_name?: string;
  submission_status?: string;
  status: 'new' | 'contacted' | 'qualified' | 'quoted' | 'won' | 'lost';
  file_uploads?: any;
  created_at: string;
  updated_at: string;
}

// Database operations
const fetchLeads = async (): Promise<Lead[]> => {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

const updateLeadStatus = async (id: string, status: string): Promise<void> => {
  const { error } = await supabase
    .from('leads')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  
  if (error) throw error;
};

export default function LeadsManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'contacted' | 'qualified' | 'quoted' | 'won' | 'lost'>('all')
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)

  const queryClient = useQueryClient();

  // Fetch leads from database
  const { data: leads = [], isLoading, error } = useQuery({
    queryKey: ['leads'],
    queryFn: fetchLeads
  });

  // Update lead status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => 
      updateLeadStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.client_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.service_requested?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (status: Lead['status']) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800'
      case 'contacted': return 'bg-yellow-100 text-yellow-800'
      case 'qualified': return 'bg-purple-100 text-purple-800'
      case 'quoted': return 'bg-orange-100 text-orange-800'
      case 'won': return 'bg-green-100 text-green-800'
      case 'lost': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      // Implementation would require delete mutation
    }
  }

  const handleStatusChange = (id: string, newStatus: Lead['status']) => {
    updateStatusMutation.mutate({ id, status: newStatus });
  }

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsViewDialogOpen(true);
  }

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    won: leads.filter(l => l.status === 'won').length,
    conversionRate: leads.length > 0 ? Math.round((leads.filter(l => l.status === 'won').length / leads.length) * 100) : 0
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Leads</h2>
          <p className="text-gray-600">Unable to load leads data. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Leads Management</h1>
          <p className="text-muted-foreground">
            Track and manage customer inquiries and sales opportunities.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="min-h-[44px] px-4">
            <Calendar className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Schedule Follow-up</span>
            <span className="sm:hidden">Schedule</span>
          </Button>
          <Button className="min-h-[44px] px-4">
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Add Lead</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All customer inquiries
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Leads</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.new}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting contact
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Won Leads</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.won}</div>
            <p className="text-xs text-muted-foreground">
              Successful conversions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.conversionRate}%</div>
            <p className="text-xs text-muted-foreground">
              Lead to customer
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Pipeline</CardTitle>
          <CardDescription>
            Manage customer inquiries and track sales opportunities.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col sm:flex-row flex-1 gap-3">
              <div className="relative flex-1 max-w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 min-h-[44px]"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-3 py-2 border border-input rounded-md bg-background text-sm min-h-[44px] min-w-[140px]"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="quoted">Quoted</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <div className="flex gap-3 mt-2 sm:mt-0">
              <Button variant="outline" size="sm" className="min-h-[44px] px-4">
                <Filter className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">More Filters</span>
                <span className="sm:hidden">Filters</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0 md:p-0">
          <ResponsiveTable>
            <ResponsiveTableHeader>
              <ResponsiveTableRow mobileCard={false}>
                <ResponsiveTableHeaderCell>Contact</ResponsiveTableHeaderCell>
                <ResponsiveTableHeaderCell>Service Interest</ResponsiveTableHeaderCell>
                <ResponsiveTableHeaderCell>Location</ResponsiveTableHeaderCell>
                <ResponsiveTableHeaderCell>Status</ResponsiveTableHeaderCell>
                <ResponsiveTableHeaderCell>Form Source</ResponsiveTableHeaderCell>
                <ResponsiveTableHeaderCell>Created</ResponsiveTableHeaderCell>
                <ResponsiveTableHeaderCell className="w-[100px]">Actions</ResponsiveTableHeaderCell>
              </ResponsiveTableRow>
            </ResponsiveTableHeader>
            <ResponsiveTableBody>
              {filteredLeads.map((lead) => (
                <ResponsiveTableRow key={lead.id}>
                  <ResponsiveTableCell label="Contact">
                    <div className="space-y-1">
                      <div className="font-medium">
                        {lead.client_name}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-3 w-3" />
                        <span className="truncate">{lead.client_email}</span>
                      </div>
                      {lead.client_phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {lead.client_phone}
                        </div>
                      )}
                    </div>
                  </ResponsiveTableCell>
                  <ResponsiveTableCell label="Service Interest">
                    <div className="space-y-2">
                      {lead.service_requested && (
                        <Badge variant="outline" className="text-xs">
                          {lead.service_requested}
                        </Badge>
                      )}
                      {lead.message && (
                        <div className="text-xs text-muted-foreground line-clamp-3">
                          {lead.message}
                        </div>
                      )}
                    </div>
                  </ResponsiveTableCell>
                  <ResponsiveTableCell label="Location">
                    <div className="text-sm space-y-1">
                      {lead.service_street && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{lead.service_street}</span>
                        </div>
                      )}
                      {lead.location && (
                        <div className="text-xs text-muted-foreground">
                          {lead.location}
                        </div>
                      )}
                    </div>
                  </ResponsiveTableCell>
                  <ResponsiveTableCell label="Status">
                    <Badge className={getStatusColor(lead.status)}>
                      {lead.status}
                    </Badge>
                  </ResponsiveTableCell>
                  <ResponsiveTableCell label="Form Source">
                    <div className="text-sm space-y-1">
                      <div>{lead.form_name || lead.form_id || 'Contact Form'}</div>
                      {lead.submission_url && (
                        <div className="text-xs text-muted-foreground truncate">
                          {new URL(lead.submission_url).pathname}
                        </div>
                      )}
                    </div>
                  </ResponsiveTableCell>
                  <ResponsiveTableCell label="Created">
                    <div className="text-sm text-muted-foreground">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </div>
                  </ResponsiveTableCell>
                  <ResponsiveTableCell label="Actions">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="min-h-[44px] px-4"
                        onClick={() => handleViewLead(lead)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        <span className="md:hidden">View</span>
                      </Button>
                      <Select value={lead.status} onValueChange={(value) => handleStatusChange(lead.id, value as Lead['status'])}>
                        <SelectTrigger className="w-full sm:w-32 min-h-[44px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="quoted">Quoted</SelectItem>
                          <SelectItem value="won">Won</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </ResponsiveTableCell>
                </ResponsiveTableRow>
              ))}
            </ResponsiveTableBody>
          </ResponsiveTable>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Management Tools</CardTitle>
          <CardDescription>
            Quick access to common lead management tasks and workflows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600 flex-shrink-0">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Follow Up</div>
                  <div className="text-sm text-muted-foreground">Contact pending leads</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-600 flex-shrink-0">
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Sales Report</div>
                  <div className="text-sm text-muted-foreground">View conversion metrics</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600 flex-shrink-0">
                  <Calendar className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Schedule Calls</div>
                  <div className="text-sm text-muted-foreground">Book appointments</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 min-h-[64px] justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-600 flex-shrink-0">
                  <Plus className="h-4 w-4" />
                </div>
                <div className="text-left min-w-0">
                  <div className="font-medium">Import Leads</div>
                  <div className="text-sm text-muted-foreground">Bulk import from CSV</div>
                </div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* View Lead Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>
              Complete information for this customer inquiry
            </DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h4 className="font-semibold mb-3">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Name</label>
                    <p className="text-sm">{selectedLead.client_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-sm">{selectedLead.client_email}</p>
                  </div>
                  {selectedLead.client_phone && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-sm">{selectedLead.client_phone}</p>
                    </div>
                  )}
                  {selectedLead.service_street && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Service Address</label>
                      <p className="text-sm">{selectedLead.service_street}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Service Details */}
              <div>
                <h4 className="font-semibold mb-3">Service Request</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedLead.service_requested && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Service Type</label>
                      <p className="text-sm">{selectedLead.service_requested}</p>
                    </div>
                  )}
                  {selectedLead.location && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Location</label>
                      <p className="text-sm">{selectedLead.location}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <Badge className={getStatusColor(selectedLead.status)}>
                        {selectedLead.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {selectedLead.message && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-500">Customer Message</label>
                    <p className="text-sm mt-1 p-3 bg-gray-50 rounded-md">
                      {selectedLead.message}
                    </p>
                  </div>
                )}
              </div>

              {/* Form Details */}
              <div>
                <h4 className="font-semibold mb-3">Submission Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Form Source</label>
                    <p className="text-sm">{selectedLead.form_name || selectedLead.form_id || 'Contact Form'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Submitted</label>
                    <p className="text-sm">{new Date(selectedLead.created_at).toLocaleString()}</p>
                  </div>
                  {selectedLead.submission_url && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">Submission URL</label>
                      <p className="text-sm text-blue-600 break-all">{selectedLead.submission_url}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Files */}
              {selectedLead.file_uploads && Array.isArray(selectedLead.file_uploads) && selectedLead.file_uploads.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Uploaded Files</h4>
                  <div className="space-y-2">
                    {selectedLead.file_uploads.map((file: any, index: number) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                        <span className="text-sm">{file.name || `File ${index + 1}`}</span>
                        {file.url && (
                          <Button size="sm" variant="outline" asChild>
                            <a href={file.url} target="_blank" rel="noopener noreferrer">
                              View
                            </a>
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                  {selectedLead.client_phone && (
                    <Button variant="outline" size="sm">
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Select value={selectedLead.status} onValueChange={(value) => handleStatusChange(selectedLead.id, value as Lead['status'])}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="won">Won</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}